import { useCallback, useContext } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useParams, generatePath } from 'react-router-dom';
import { pipe, Array as A } from 'effect';

import { completeResetAction, locationSelector, querySelector } from 'domain/router';
import { navigate } from 'domain/router/redux/reduxActions';
import { cleanQuery, queryToSearch } from 'domain/router/helpers';
import ROUTES_PATH from 'domain/router/routesPathConfig';
import { getNestedCategory, findCatById } from 'domain/categories/helpers';
import { doctypeTabLookupSelector, categoriesListSelector } from 'domain/categories/selectors';
import { subtypeFilterSelector, setSubtypeFilterAction, documentsGridFilterAppliedAction } from 'domain/documents';
import { gridCurrentFiltersSelector } from 'domain/documents/documentSelector';
import { isGridWorkSpaceSelector } from 'domain/companies';
import CONST from 'domain/documents/constants';
import CompanyWorkspaceContext from 'pages/company/WorkspaceRoute/CompanyWorkspaceContext';
import { CLEARED_WORKSPACE_QUERY } from 'pages/company/WorkspaceRoute/components/WorkspaceSearch/querySchema';

// Everything the empty screens need: which view to show, and the way out of it.
//
// The Workspace narrows the document list on four independent levels (da-19295 D9 filter cascade):
// the global URL filter (search chips / date ranges), the routed doctype tab (D7), the Inbox
// sub-type (Redux `documents.subtypeFilter`, D2) and — grid only — the AG-Grid column filterModel.
// `resetFilters` is the single escape hatch behind the no-results button, and it clears all four.
// Clearing only the tab + sub-type would dead-end the click whenever a level ABOVE them zeroed the
// list — the category counts already reflect the global filter, so the user would click and land on
// the very same empty screen.
export const useWorkspaceEmptyState = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useSelector(locationSelector);
  const rawQuery = useSelector(querySelector);
  const subtype = useSelector(subtypeFilterSelector);
  const doctypeLookup = useSelector(doctypeTabLookupSelector);
  const isGridWorkspace = useSelector(isGridWorkSpaceSelector);
  const gridFiltersRaw = useSelector(gridCurrentFiltersSelector);
  const categoriesList = useSelector(categoriesListSelector);
  const { companyType } = useContext(CompanyWorkspaceContext);

  // Grid-column filters only count on the grid workspace (mirrors useWorkspaceSearch).
  const hasGridFilters = isGridWorkspace && Object.keys(gridFiltersRaw).length > 0;
  const hasQuery = Object.values(rawQuery).some((item) => (Array.isArray(item) ? item.some(Boolean) : Boolean(item)));

  // Active tab = the deepest URL category segment resolved against the stable all-categories lookup
  // (never the aggregations, which lag one navigation) — same derivation as WorkspaceDoctypeTabs.
  const activeDoctype = doctypeLookup.get(String(getNestedCategory(params))) ?? 'all';
  const onDoctypeSegment = activeDoctype === CONST.doctype.financial || activeDoctype === CONST.doctype.general;

  const template =
    companyType === 'confidential'
      ? ROUTES_PATH.COMPANY_CONFIDENTIAL_WORKSPACE.absolute
      : ROUTES_PATH.COMPANY_WORKSPACE.absolute;
  // Tab → All is the removal of its segment; the category / sub-category path is preserved.
  const filled = pipe(
    [params.category1, params.category2, params.category3],
    A.filter((segment): segment is string => Boolean(segment)),
  );
  const [category1, category2, category3] = onDoctypeSegment ? filled.slice(0, -1) : filled;
  const pathname = generatePath(template, { companyId: params.companyId, category1, category2, category3 });

  // Does this category hold documents at all? `getWorkspaceCategories` carries the global URL filter
  // and NOTHING else — no doctype tab, no sub-type, no AG-Grid filterModel (see categories/sagas.js)
  // — so its per-category `count` answers exactly that, for every company and every category. It is
  // deliberately NOT the All doctype tab's counter: that one exists only for a Bookkeeper AND on a
  // tab-eligible category, and reading it would make the empty screen of every other role and
  // category depend on a layer they do not have.
  // Read off the BASE category — the tab segment is stripped above, and a doctype tab's own count is
  // zero by definition on the very screen this answers for.
  const categoryHasDocs = Boolean(findCatById(categoriesList, category3 ?? category2 ?? category1)?.count);

  // One React commit for every level, per the useWorkspaceSearch.clearAll precedent: the grid filter
  // and the sub-type are synchronous reducer writes while the query lives in the URL (async via the
  // `updateQuery` saga), so plain sequential dispatches clear them in separate visible waves.
  // `completeResetAction` mirrors that saga (it also zeroes the env loading counter, so a search
  // cancelled by the push cannot leave the loader stuck); the push is guarded so a sub-type-only
  // reset does not re-push an identical location.
  const resetFilters = useCallback(() => {
    const previousSearch = queryToSearch(cleanQuery({ ...rawQuery }));
    const search = queryToSearch(cleanQuery({ ...rawQuery, ...CLEARED_WORKSPACE_QUERY }));
    batch(() => {
      if (subtype) dispatch(setSubtypeFilterAction(null));
      if (hasGridFilters) dispatch(documentsGridFilterAppliedAction({ filters: {} }));
      dispatch(completeResetAction());
      if (search !== previousSearch || pathname !== location.pathname) {
        dispatch(navigate.push({ pathname, search }));
      }
    });
  }, [dispatch, rawQuery, subtype, hasGridFilters, pathname, location.pathname]);

  return {
    hasQuery,
    categoryHasDocs,
    resetFilters,
  };
};
